/**
 * German labels for the NC-1281 verdict unions — one source for both stages.
 *
 * The stored values are the API's unions (`as-planned` … / `bewaehrt` …). The
 * conclusion union is already German because those are the words the operator
 * uses; the run union is not, so it needs a translation layer. Keeping both maps
 * here means the Run-Rueckblick dialog and the Steckbrief section cannot drift
 * into calling the same verdict two different things.
 */

import { ConclusionVerdict, RunRetroVerdict } from '../services/types';

type ChipColor = 'success' | 'warning' | 'error' | 'info' | 'default';

export const RUN_RETRO_VERDICTS: readonly RunRetroVerdict[] = [
  'as-planned',
  'with-limitations',
  'unusable',
];

export const CONCLUSION_VERDICTS: readonly ConclusionVerdict[] = [
  'bewaehrt',
  'mit-auflagen',
  'ueberarbeiten',
  'einstellen',
];

export const RUN_RETRO_LABEL: Record<RunRetroVerdict, string> = {
  'as-planned': 'Wie geplant',
  'with-limitations': 'Mit Einschränkungen',
  unusable: 'Nicht nutzbar',
};

export const CONCLUSION_LABEL: Record<ConclusionVerdict, string> = {
  bewaehrt: 'Bewährt',
  'mit-auflagen': 'Mit Auflagen',
  ueberarbeiten: 'Überarbeiten',
  einstellen: 'Einstellen',
};

export const RUN_RETRO_COLOR: Record<RunRetroVerdict, ChipColor> = {
  'as-planned': 'success',
  'with-limitations': 'warning',
  unusable: 'error',
};

export const CONCLUSION_COLOR: Record<ConclusionVerdict, ChipColor> = {
  bewaehrt: 'success',
  'mit-auflagen': 'warning',
  ueberarbeiten: 'warning',
  einstellen: 'error',
};

/**
 * Label for a value that came from storage.
 *
 * A document written by a future version can carry a verdict this build does not
 * know. Showing the raw value is better than showing nothing: the reader still
 * learns what was recorded, which matches the server's decision to count and
 * ignore unknown `kind`s rather than fail the whole read.
 */
export function runRetroLabel(verdict: string): string {
  return RUN_RETRO_LABEL[verdict as RunRetroVerdict] ?? verdict;
}

export function conclusionLabel(verdict: string): string {
  return CONCLUSION_LABEL[verdict as ConclusionVerdict] ?? verdict;
}

export function runRetroColor(verdict: string): ChipColor {
  return RUN_RETRO_COLOR[verdict as RunRetroVerdict] ?? 'default';
}

export function conclusionColor(verdict: string): ChipColor {
  return CONCLUSION_COLOR[verdict as ConclusionVerdict] ?? 'default';
}
